import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { GearSix, Plus, Trash, FloppyDisk, Palette, ListBullets, TextT, UploadSimple, Image } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/lib/api";

export function POSConfig() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [config, setConfig] = useState(null);
  const [methods, setMethods] = useState([]);
  const [newMethod, setNewMethod] = useState("");
  const [tab, setTab] = useState("datos");
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef(null);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/pos/config`, { headers }),
      axios.get(`${API}/pos/payment-methods`, { headers }),
    ]).then(([cfgRes, pmRes]) => {
      setConfig(cfgRes.data);
      setMethods(pmRes.data);
    }).catch(() => toast.error("Error al cargar configuracion"));
  }, [token]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/pos/config`, config, { headers });
      setConfig(res.data);
      toast.success("Configuracion guardada");
    } catch { toast.error("Error al guardar"); }
    setSaving(false);
  };

  const addMethod = async () => {
    if (!newMethod.trim()) return;
    try {
      const res = await axios.post(`${API}/pos/payment-methods`, { name: newMethod.trim() }, { headers });
      setMethods([...methods, res.data]);
      setNewMethod("");
      toast.success("Metodo agregado");
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const deleteMethod = async (id) => {
    try {
      await axios.delete(`${API}/pos/payment-methods/${id}`, { headers });
      setMethods(methods.filter(m => m.id !== id));
      toast.success("Eliminado");
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/pos/config/logo`, fd, { headers: { ...headers, "Content-Type": "multipart/form-data" } });
      set("logo_url", res.data.logo_url);
      toast.success("Logo subido correctamente");
    } catch { toast.error("Error al subir logo"); }
    setUploadingLogo(false);
  };

  const set = (field, value) => setConfig(prev => ({ ...prev, [field]: value }));
  const setNested = (parent, field, value) => setConfig(prev => ({ ...prev, [parent]: { ...prev[parent], [field]: value } }));

  if (!config) return <div className="flex items-center justify-center py-20"><div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full" /></div>;

  return (
    <div className="space-y-5 max-w-5xl" data-testid="pos-config">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><GearSix size={24} weight="duotone" className="text-blue-600" /> Configuracion POS</h1>
          <p className="text-sm text-slate-500">Datos fiscales, plantillas y metodos de pago</p>
        </div>
        <Button onClick={save} disabled={saving} data-testid="pos-config-save">
          <FloppyDisk size={16} className="mr-2" /> {saving ? "Guardando..." : "Guardar"}
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 flex-wrap">
        {[{id:"datos",label:"Datos Fiscales",icon:ListBullets},{id:"logo",label:"Logo",icon:Image},{id:"plantillas",label:"Plantillas PDF",icon:Palette},{id:"textos",label:"Textos",icon:TextT},{id:"pagos",label:"Metodos de Pago",icon:ListBullets}].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex items-center gap-1.5 px-4 py-2 text-xs font-semibold rounded-md transition-colors ${tab === t.id ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
            data-testid={`pos-cfg-tab-${t.id}`}>
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {/* Tab: Datos Fiscales */}
      {tab === "datos" && (
        <div className="bg-white border rounded-xl p-6 space-y-4">
          <h3 className="text-sm font-bold text-slate-800 uppercase">Datos del Emisor</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><label className="text-[10px] font-semibold text-slate-500 uppercase">Razon Social</label><Input value={config.company_name || ""} onChange={e => set("company_name", e.target.value)} className="h-9 text-sm mt-1" data-testid="cfg-company" /></div>
            <div><label className="text-[10px] font-semibold text-slate-500 uppercase">RFC</label><Input value={config.rfc || ""} onChange={e => set("rfc", e.target.value)} className="h-9 text-sm mt-1" data-testid="cfg-rfc" /></div>
            <div className="md:col-span-2"><label className="text-[10px] font-semibold text-slate-500 uppercase">Domicilio Fiscal</label><Input value={config.address || ""} onChange={e => set("address", e.target.value)} className="h-9 text-sm mt-1" /></div>
            <div><label className="text-[10px] font-semibold text-slate-500 uppercase">Codigo Postal</label><Input value={config.postal_code || ""} onChange={e => set("postal_code", e.target.value)} className="h-9 text-sm mt-1" /></div>
            <div><label className="text-[10px] font-semibold text-slate-500 uppercase">Telefono</label><Input value={config.phone || ""} onChange={e => set("phone", e.target.value)} className="h-9 text-sm mt-1" /></div>
            <div><label className="text-[10px] font-semibold text-slate-500 uppercase">Email</label><Input value={config.email || ""} onChange={e => set("email", e.target.value)} className="h-9 text-sm mt-1" /></div>
            <div><label className="text-[10px] font-semibold text-slate-500 uppercase">Regimen Fiscal</label><Input value={config.tax_regime || ""} onChange={e => set("tax_regime", e.target.value)} className="h-9 text-sm mt-1" /></div>
          </div>
        </div>
      )}

      {/* Tab: Logo */}
      {tab === "logo" && (
        <div className="bg-white border rounded-xl p-6 space-y-5">
          <h3 className="text-sm font-bold text-slate-800 uppercase">Logo de la Empresa</h3>
          <p className="text-xs text-slate-500">El logo aparecera a la izquierda en los PDFs y en las vistas publicas de ventas.</p>
          <div className="flex items-start gap-6">
            <div className="w-32 h-32 border-2 border-dashed border-slate-300 rounded-xl flex items-center justify-center bg-slate-50 overflow-hidden shrink-0">
              {config.logo_url ? (
                <img src={config.logo_url} alt="Logo" className="w-full h-full object-contain p-2" data-testid="cfg-logo-preview" />
              ) : (
                <Image size={40} className="text-slate-300" />
              )}
            </div>
            <div className="space-y-3 flex-1">
              <input ref={logoInputRef} type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" data-testid="cfg-logo-input" />
              <Button variant="outline" onClick={() => logoInputRef.current?.click()} disabled={uploadingLogo} data-testid="cfg-logo-upload-btn">
                <UploadSimple size={16} className="mr-2" /> {uploadingLogo ? "Subiendo..." : "Subir Logo"}
              </Button>
              {config.logo_url && (
                <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => { set("logo_url", ""); save(); }} data-testid="cfg-logo-remove">
                  <Trash size={14} className="mr-2" /> Eliminar Logo
                </Button>
              )}
              <p className="text-[10px] text-slate-400">Formatos: PNG, JPG, SVG. Maximo 2MB recomendado.</p>
            </div>
          </div>
        </div>
      )}

      {/* Tab: Plantillas PDF */}
      {tab === "plantillas" && (
        <div className="space-y-5">
          {/* Template selector */}
          <div className="grid grid-cols-3 gap-4">
            {[
              { id: "basica", name: "Basica", desc: "Minimalista, sin colores ni logo" },
              { id: "intermedia", name: "Intermedia", desc: "Logo, barra de color, tabla con bordes" },
              { id: "avanzada", name: "Avanzada", desc: "Header completo, cliente en caja, footer" },
            ].map(t => (
              <button key={t.id} onClick={() => set("active_template", t.id)}
                className={`border-2 rounded-xl p-5 text-left transition-all ${config.active_template === t.id ? "border-blue-500 bg-blue-50 ring-2 ring-blue-200" : "border-slate-200 hover:border-blue-300"}`}
                data-testid={`tpl-${t.id}`}>
                <p className="text-sm font-bold text-slate-900">{t.name}</p>
                <p className="text-xs text-slate-500 mt-1">{t.desc}</p>
                {config.active_template === t.id && <div className="mt-2 text-[10px] font-bold text-blue-600">✓ Seleccionada</div>}
              </button>
            ))}
          </div>

          {/* Colors */}
          <div className="bg-white border rounded-xl p-5">
            <h3 className="text-xs font-semibold text-slate-500 uppercase mb-4">Colores</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { key: "primary", label: "Color Primario" },
                { key: "accent", label: "Color Acento" },
                { key: "header_bg", label: "Fondo Header" },
                { key: "text", label: "Color Texto" },
              ].map(c => (
                <div key={c.key} className="flex items-center gap-2">
                  <input type="color" value={config.template_colors?.[c.key] || "#000000"} onChange={e => setNested("template_colors", c.key, e.target.value)}
                    className="w-8 h-8 rounded-lg border cursor-pointer shrink-0" />
                  <div>
                    <p className="text-[10px] font-semibold text-slate-500">{c.label}</p>
                    <p className="text-[10px] font-mono text-slate-400">{config.template_colors?.[c.key]}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Structure */}
          <div className="bg-white border rounded-xl p-5">
            <h3 className="text-xs font-semibold text-slate-500 uppercase mb-4">Estructura</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {[
                { key: "show_logo", label: "Mostrar Logo" },
                { key: "show_rfc", label: "Mostrar RFC" },
                { key: "show_address", label: "Mostrar Direccion" },
                { key: "show_regime", label: "Mostrar Regimen Fiscal" },
                { key: "table_borders", label: "Tabla con bordes" },
              ].map(s => (
                <label key={s.key} className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={config.template_structure?.[s.key] ?? true} onChange={e => setNested("template_structure", s.key, e.target.checked)}
                    className="w-4 h-4 rounded border-slate-300 text-blue-600" />
                  <span className="text-xs text-slate-700">{s.label}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Tab: Textos */}
      {tab === "textos" && (
        <div className="space-y-4">
          {[
            { field: "payment_terms", toggle: "include_payment_terms", label: "Condiciones de Pago" },
            { field: "sale_notes", toggle: "include_sale_notes", label: "Notas de Venta" },
            { field: "warranty_notes", toggle: "include_warranty_notes", label: "Garantia" },
            { field: "terms_conditions", toggle: "include_terms_conditions", label: "Terminos y Condiciones" },
          ].map(s => (
            <div key={s.field} className="bg-white border rounded-xl p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold text-slate-800">{s.label}</h3>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={config[s.toggle] ?? true} onChange={e => set(s.toggle, e.target.checked)} className="w-4 h-4 rounded border-slate-300 text-blue-600" />
                  <span className="text-[10px] font-semibold text-slate-500">Incluir en PDF</span>
                </label>
              </div>
              <Textarea value={config[s.field] || ""} onChange={e => set(s.field, e.target.value)} className="text-sm min-h-[80px]" placeholder={`Escribe ${s.label.toLowerCase()}...`} />
            </div>
          ))}
        </div>
      )}

      {/* Tab: Metodos de Pago */}
      {tab === "pagos" && (
        <div className="bg-white border rounded-xl p-5 space-y-4">
          <h3 className="text-sm font-bold text-slate-800 uppercase">Metodos de Pago</h3>
          <div className="space-y-2">
            {methods.map(m => (
              <div key={m.id} className="flex items-center justify-between p-3 border rounded-lg group hover:border-blue-200">
                <span className="text-sm font-medium text-slate-800">{m.name}</span>
                <button onClick={() => deleteMethod(m.id)} className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-500 transition-all"><Trash size={14} /></button>
              </div>
            ))}
          </div>
          <div className="flex gap-2 pt-2 border-t">
            <Input value={newMethod} onChange={e => setNewMethod(e.target.value)} onKeyDown={e => e.key === "Enter" && addMethod()}
              placeholder="Nombre del nuevo metodo..." className="h-9 text-sm flex-1" data-testid="pos-new-method" />
            <Button size="sm" className="h-9" onClick={addMethod} data-testid="pos-add-method"><Plus size={14} className="mr-1" /> Agregar</Button>
          </div>
        </div>
      )}
    </div>
  );
}
